import React from 'react'
import { Dropdown, Icon, Popup, Checkbox, Message } from 'semantic-ui-react'
import { FETCH_KEG_COLLECTION_BOOKING_COLLECTORS } from '../../../../../../redux/actions/actionTypes'
import { useCollectorOptions } from '../../../utils/useCollectorOptions'

const subsidiaryIdsByCountryCode = {
  21: ['au'],
  22: ['nz'],
  '24_25_26': ['uk', 'ie', 'nl', 'gb'],
}

const getSubsidiaryIds = countryCode => {
  const cc = countryCode ? countryCode.toLowerCase() : ''
  const entry = Object.entries(subsidiaryIdsByCountryCode).find(([, countryCodes]) =>
    Array.isArray(countryCodes) ? countryCodes.includes(cc) : countryCodes === cc
  )
  return entry ? entry[0] : null
}

const getOptionsWithOrphaned = (collectorOptions, hasMatch, orphanedId, translate) => {
  if (orphanedId && !hasMatch) {
    const orphanOption = {
      key: `orphan-${orphanedId}`,
      value: `__orphan__:${orphanedId}`,
      text: `${translate('collectorOrphanedOption')} ${orphanedId}`,
      disabled: true,
      className: 'orphan-option',
    }
    const divider = {
      key: `divider-orphan-${orphanedId}`,
      value: `__divider__:${orphanedId}`,
      text: '────────────',
      disabled: true,
    }
    return [orphanOption, divider, ...collectorOptions]
  }
  return collectorOptions
}

const CollectorsDropdown = props => {
  const {
    dispatch,
    createAction,
    collectorsList: { content, isFetching, didFetchSucceed, didFetchFail },
    collectorCustomerId,
    collectorCustomerName,
    collectorCustomerTapId,
    bookingCountryCode,
    collectorDropdownValue,
    onChangeCallback,
    isDisabled,
    translate,
    handlePreferredCollectorCheckboxChange,
    setAsPreferredCollector,
    isPreferredCollector,
  } = props

  React.useEffect(() => {
    // No booking details exist, collectors have already been fetched, or are currently being fetched, return
    if (!bookingCountryCode || didFetchSucceed || isFetching) return
    // User has deep linked to a booking, no collectors exist, fetch the collectors
    if (!content || !Array.isArray(content) || content.length === 0) {
      dispatch(
        createAction(FETCH_KEG_COLLECTION_BOOKING_COLLECTORS, {
          customerParams: {
            ct: 'COL',
            subsidiaryIds: getSubsidiaryIds(bookingCountryCode),
            sts: 'ACT',
            includeDSHP: true,
          },
        })
      )
    }
  }, [bookingCountryCode, content, dispatch, createAction, didFetchSucceed, isFetching])

  const baseOptions = useCollectorOptions(content, translate)

  const hasMatch = React.useMemo(
    () => Boolean(collectorCustomerId) && baseOptions.some(o => o.value === collectorCustomerId),
    [collectorCustomerId, baseOptions]
  )

  const options = React.useMemo(
    () => getOptionsWithOrphaned(baseOptions, hasMatch, collectorCustomerTapId, translate),
    [baseOptions, hasMatch, collectorCustomerTapId, translate]
  )

  const [selectedValue, setSelectedValue] = React.useState(collectorDropdownValue)
  const [showOrphanedMessage, setShowOrphanedMessage] = React.useState(false)

  React.useEffect(() => {
    if (!bookingCountryCode) return
    if (hasMatch) {
      setSelectedValue(collectorCustomerId)
      setShowOrphanedMessage(false)
    } else {
      setSelectedValue('')
      setShowOrphanedMessage(Boolean(collectorCustomerId))
    }
  }, [collectorCustomerId, hasMatch, bookingCountryCode, baseOptions])

  const handleDropDownChange = (_, data) => {
    setSelectedValue(data.value)
    setShowOrphanedMessage(false)
    onChangeCallback(data.value)
  }

  const isDisabledFinal = isDisabled || isFetching || didFetchFail || !Array.isArray(content)

  return (
    <>
      <label htmlFor="bookingCollectorDropdown">{translate('Collector')}</label>
      <Dropdown
        id="bookingCollectorDropdown"
        selection
        search
        fluid
        clearable
        options={options}
        loading={isFetching}
        disabled={isDisabledFinal}
        placeholder={
          isFetching
            ? translate('loadingCollectors')
            : content.length === 0
              ? translate('noCollectorsAvailable')
              : translate('selectCollector')
        }
        value={selectedValue}
        onChange={handleDropDownChange}
      />
      <div style={{ marginTop: '6px' }}>
        <Popup
          content={translate('preferredCollectorInfoTooltip')}
          trigger={
            <Icon name="info circle" aria-label={translate('preferredCollectorInfoTooltip')} />
          }
          disabled={isDisabledFinal || !selectedValue || isPreferredCollector}
        />
        <Checkbox
          label={translate('preferredCollectorCheckboxLabel')}
          checked={setAsPreferredCollector}
          onChange={(_, { checked }) => handlePreferredCollectorCheckboxChange(checked)}
          disabled={isDisabledFinal || !selectedValue || isPreferredCollector}
        />
      </div>
      <div style={{ marginTop: '0.5em' }}>
        {showOrphanedMessage && (
          <Message color="yellow">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5em' }}>
              <span>{translate('orphanedCollectorMessage')}</span>
              <span style={{ fontWeight: '600' }}>{collectorCustomerName}</span>
              <span style={{ fontWeight: '600' }}>TAP ID: {collectorCustomerTapId}</span>
            </div>
          </Message>
        )}
        {didFetchFail && <Message negative content={translate('collectorsLoadError')} />}
      </div>
    </>
  )
}

export default React.memo(CollectorsDropdown)
